import { computed, ref } from 'vue'
import type { DonorDonation } from '@/types/donorFinancialYear'

/**
 * Donation detail and recurring donation detail share one modal.
 * Each forward step pushes a frame. Back removes the top frame.
 * Close clears the stack, whatever the depth.
 * Earlier frames stay mounted by the host, so scroll and expanded
 * sections survive the round trip.
 */
export type DonationDetailFrame = {
  key: string
  kind: 'donation'
  donation: DonorDonation
}

export type RecurringDetailFrame = {
  key: string
  kind: 'recurring'
  seriesId: string
}

export type DetailFrame = DonationDetailFrame | RecurringDetailFrame

const stack = ref<DetailFrame[]>([])

let nextKey = 0

function frameKey() {
  nextKey += 1
  return `detail-${nextKey}`
}

function donationFrame(donation: DonorDonation): DonationDetailFrame {
  return { key: frameKey(), kind: 'donation', donation }
}

function recurringFrame(seriesId: string): RecurringDetailFrame {
  return { key: frameKey(), kind: 'recurring', seriesId }
}

/** A list click starts a new modal. Anything already open is replaced. */
function openRootDonation(donation: DonorDonation) {
  stack.value = [donationFrame(donation)]
}

function openRootRecurring(seriesId: string) {
  stack.value = [recurringFrame(seriesId)]
}

/**
 * Move from the current detail to another. If that view is already in the
 * stack, pop back to it instead of opening a second copy.
 */
function pushDonation(donation: DonorDonation) {
  if (!stack.value.length) {
    openRootDonation(donation)
    return
  }
  const existing = stack.value.findIndex(
    (frame) => frame.kind === 'donation' && frame.donation.id === donation.id
  )
  if (existing >= 0) {
    const next = stack.value.slice(0, existing + 1)
    const frame = next[existing]
    if (frame?.kind === 'donation') frame.donation = donation
    stack.value = next
    return
  }
  stack.value = [...stack.value, donationFrame(donation)]
}

function pushRecurring(seriesId: string) {
  if (!stack.value.length) {
    openRootRecurring(seriesId)
    return
  }
  const existing = stack.value.findIndex(
    (frame) => frame.kind === 'recurring' && frame.seriesId === seriesId
  )
  if (existing >= 0) {
    stack.value = stack.value.slice(0, existing + 1)
    return
  }
  stack.value = [...stack.value, recurringFrame(seriesId)]
}

function backDetail() {
  if (stack.value.length <= 1) return
  stack.value = stack.value.slice(0, -1)
}

function closeDetailNavigation() {
  stack.value = []
}

function patchDetailDonation(donation: DonorDonation) {
  stack.value = stack.value.map((frame) =>
    frame.kind === 'donation' && frame.donation.id === donation.id
      ? { ...frame, donation }
      : frame
  )
}

/** Drop one gift, wherever it sits, without popping whatever was opened after it. */
function removeDonationFromNavigation(donationId: string) {
  stack.value = stack.value.filter(
    (frame) => frame.kind !== 'donation' || frame.donation.id !== donationId
  )
}

/** Drop the deleted schedule and the gifts that belonged to it. */
function removeSeriesFromNavigation(seriesId: string) {
  stack.value = stack.value.filter((frame) => {
    if (frame.kind === 'recurring') return frame.seriesId !== seriesId
    return frame.donation.recurringSeriesId !== seriesId
  })
}

export function useDetailNavigation() {
  const root = computed(() => stack.value[0] ?? null)
  const top = computed(() => stack.value[stack.value.length - 1] ?? null)
  const canGoBack = computed(() => stack.value.length > 1)

  return {
    stack,
    root,
    top,
    canGoBack,
    openRootDonation,
    openRootRecurring,
    pushDonation,
    pushRecurring,
    backDetail,
    closeDetailNavigation,
    patchDetailDonation,
    removeDonationFromNavigation,
    removeSeriesFromNavigation,
  }
}
